import { IMultiLanguageValue } from '../../../../common/interfaces/MultiLanguageValue.interface';
import { ValidationRules, ValidationResult } from '../types';

/**
 * Get the value for a specific language from multi-language array
 * @param fieldData - Array of multi-language values
 * @param languageCode - Language code to retrieve
 * @returns The value for the specified language or empty string
 */
export const getMultiLangFieldValue = (
  fieldData: IMultiLanguageValue[] = [],
  languageCode: string
): string => {
  return fieldData.find((item) => item.language === languageCode)?.value || '';
};

/**
 * Update the value for a specific language in multi-language array
 * @param fieldData - Existing array of multi-language values
 * @param languageCode - Language code to update
 * @param value - New value
 * @param autoCleanEmpty - Whether to remove entries with empty values
 * @returns Updated array
 */
export const updateMultiLangFieldData = (
  fieldData: IMultiLanguageValue[] = [],
  languageCode: string,
  value: string,
  autoCleanEmpty = true
): IMultiLanguageValue[] => {
  const existingEntry = fieldData.find(
    (item) => item.language === languageCode
  );

  const updatedData = existingEntry
    ? fieldData.map((item) =>
        item.language === languageCode ? { ...item, value } : item
      )
    : [...fieldData, { language: languageCode, value }];

  // Filter out empty values if autoCleanEmpty is true
  if (autoCleanEmpty) {
    return updatedData.filter((item) => item.value.trim() !== '');
  }

  return updatedData;
};

/**
 * Check if a value exists for a specific language
 * @param fieldData - Array of multi-language values
 * @param languageCode - Language code to check
 * @returns true if value exists and is not empty
 */
export const hasValueForLanguage = (
  fieldData: IMultiLanguageValue[] = [],
  languageCode: string
): boolean => {
  const value = getMultiLangFieldValue(fieldData, languageCode);
  return value.trim() !== '';
};

/**
 * Get all language codes that have non-empty values
 * @param fieldData - Array of multi-language values
 * @returns Array of language codes with values
 */
export const getLanguagesWithValues = (
  fieldData: IMultiLanguageValue[] = []
): string[] => {
  return fieldData
    .filter((item) => item.value.trim() !== '')
    .map((item) => item.language);
};

/**
 * Clear value for a specific language
 * @param fieldData - Array of multi-language values
 * @param languageCode - Language code to clear
 * @returns Updated array without the specified language
 */
export const clearLanguageValue = (
  fieldData: IMultiLanguageValue[] = [],
  languageCode: string
): IMultiLanguageValue[] => {
  return fieldData.filter((item) => item.language !== languageCode);
};

/**
 * Check if at least one language has a valid value
 * @param fieldData - Array of multi-language values
 * @returns true if at least one value exists
 */
export const hasAnyValue = (fieldData: IMultiLanguageValue[] = []): boolean => {
  return fieldData.some((item) => item.value.trim() !== '');
};

/**
 * Validate a field value against validation rules
 * @param value - Value to validate
 * @param rules - Validation rules
 * @returns Validation result with isValid flag and optional error message
 */
export const validateMultiLangField = (
  value: string,
  rules: ValidationRules
): ValidationResult => {
  // Min length validation
  if (rules.minLength !== undefined && value.length < rules.minLength) {
    return {
      isValid: false,
      error:
        rules.customMessage ||
        `Minimum length is ${rules.minLength} characters`,
    };
  }

  // Max length validation
  if (rules.maxLength !== undefined && value.length > rules.maxLength) {
    return {
      isValid: false,
      error:
        rules.customMessage ||
        `Maximum length is ${rules.maxLength} characters`,
    };
  }

  // Pattern validation
  if (rules.pattern && !rules.pattern.test(value)) {
    return {
      isValid: false,
      error: rules.customMessage || 'Invalid format',
    };
  }

  // Custom validation function
  if (rules.custom && !rules.custom(value)) {
    return {
      isValid: false,
      error: rules.customMessage || 'Validation failed',
    };
  }

  return { isValid: true };
};

/**
 * Check if multi-language field has valid values based on rules
 * @param fieldData - Array of multi-language values
 * @param rules - Validation rules
 * @param required - Whether field is required
 * @returns true if field is valid
 */
export const isMultiLangFieldValid = (
  fieldData: IMultiLanguageValue[] = [],
  rules?: ValidationRules,
  required = false
): boolean => {
  // If required, must have at least one value
  if (required && !hasAnyValue(fieldData)) {
    return false;
  }

  // If not required and empty, it's valid
  if (!required && !hasAnyValue(fieldData)) {
    return true;
  }

  // Validate all non-empty values
  if (rules) {
    return fieldData
      .filter((item) => item.value.trim() !== '')
      .every((item) => validateMultiLangField(item.value, rules).isValid);
  }

  return true;
};

/**
 * Sanitize multi-language data by removing empty entries
 * @param fieldData - Array of multi-language values
 * @returns Cleaned array
 */
export const sanitizeMultiLangData = (
  fieldData: IMultiLanguageValue[] = []
): IMultiLanguageValue[] => {
  return fieldData.filter((item) => item.value.trim() !== '');
};
